/**
 * InstallPrompt — polished "Install Connection Source" banner.
 *
 * Behaviour
 * ─────────
 * • Android / Chrome / Edge: listens for `beforeinstallprompt`, defers it,
 *   shows our own styled banner after the 2nd visit, and fires the native
 *   install UI when the user taps "Install".
 * • iOS Safari: `beforeinstallprompt` never fires there — so we detect
 *   iOS + non-standalone mode and show a custom "Share ↑ → Add to Home
 *   Screen" instruction panel instead.
 * • Dismissal is remembered for 30 days in localStorage.
 * • Already-installed mode (`display-mode: standalone` or `navigator.standalone`)
 *   → prompt is suppressed entirely.
 *
 * Storage keys (all self-contained):
 *   cs_visit_count      — how many app opens we've counted
 *   cs_install_dismissed_until — ISO date until which to stay hidden
 */
import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DownloadSimple, X, DeviceMobile, ShareNetwork, Plus, Sparkle } from '@phosphor-icons/react';

const MIN_VISITS = 2;
const SNOOZE_DAYS = 30;

function isIOS() {
  const ua = window.navigator.userAgent || '';
  return /iPhone|iPad|iPod/.test(ua) && !window.MSStream;
}

function isStandalone() {
  return window.matchMedia?.('(display-mode: standalone)').matches
    || window.navigator.standalone === true;
}

function snoozeUntil() {
  const d = new Date();
  d.setDate(d.getDate() + SNOOZE_DAYS);
  return d.toISOString();
}

function isSnoozed() {
  try {
    const iso = localStorage.getItem('cs_install_dismissed_until');
    return iso && new Date(iso) > new Date();
  } catch { return false; }
}

function bumpVisit() {
  try {
    const n = parseInt(localStorage.getItem('cs_visit_count') || '0', 10) + 1;
    localStorage.setItem('cs_visit_count', String(n));
    return n;
  } catch { return 0; }
}

export default function InstallPrompt() {
  const deferredRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [mode, setMode] = useState('native'); // 'native' | 'ios'

  useEffect(() => {
    // Short-circuit conditions: already installed or user snoozed us
    if (isStandalone() || isSnoozed()) return;

    // Bump visit counter once per page-load
    const visits = bumpVisit();

    // iOS flow — no native event, just pattern-detect and show guide
    if (isIOS()) {
      if (visits >= MIN_VISITS) {
        const t = setTimeout(() => { setMode('ios'); setVisible(true); }, 4000);
        return () => clearTimeout(t);
      }
      return;
    }

    // Native flow (Android Chrome, Edge, Samsung, desktop Chrome)
    const onBIP = (e) => {
      e.preventDefault();
      deferredRef.current = e;
      if (visits >= MIN_VISITS) {
        setMode('native');
        setVisible(true);
      }
    };
    const onInstalled = () => {
      setVisible(false);
      try { localStorage.setItem('cs_install_dismissed_until', snoozeUntil()); } catch { /* noop */ }
    };
    window.addEventListener('beforeinstallprompt', onBIP);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBIP);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const install = async () => {
    const dp = deferredRef.current;
    if (!dp) { setVisible(false); return; }
    try { navigator.vibrate?.(8); } catch { /* noop */ }
    dp.prompt();
    await dp.userChoice;
    deferredRef.current = null;
    setVisible(false);
  };

  const dismiss = () => {
    try { localStorage.setItem('cs_install_dismissed_until', snoozeUntil()); } catch { /* noop */ }
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
          className="fixed left-3 right-3 z-[60] sm:left-auto sm:right-4 sm:max-w-sm"
          style={{
            bottom: 'calc(env(safe-area-inset-bottom) + 12px)',
          }}
          data-testid="install-prompt"
          role="dialog"
          aria-label="Install Connection Source"
        >
          <div
            className="cs-install-card relative overflow-hidden rounded-2xl p-4 shadow-2xl"
            style={{
              background: 'linear-gradient(135deg, rgba(205,4,11,0.16) 0%, var(--cs-bg-surface) 40%, var(--cs-bg-surface) 65%, rgba(205,4,11,0.12) 100%)',
              border: '1px solid rgba(205,4,11,0.35)',
              boxShadow: '0 24px 60px -16px rgba(205,4,11,0.35), inset 0 1px 0 rgba(255,255,255,0.05)',
              backdropFilter: 'blur(18px) saturate(180%)',
              WebkitBackdropFilter: 'blur(18px) saturate(180%)',
            }}
          >
            {/* Ambient glow orb */}
            <div
              aria-hidden
              className="absolute -top-8 -right-8 w-28 h-28 rounded-full pointer-events-none"
              style={{
                background: 'radial-gradient(circle, rgba(205,4,11,0.35), transparent 70%)',
                filter: 'blur(14px)',
              }}
            />
            {/* top shimmer */}
            <div
              aria-hidden
              className="absolute top-0 left-0 right-0 h-[2px]"
              style={{ background: 'linear-gradient(90deg, transparent, #CD040B, transparent)' }}
            />

            <button
              onClick={dismiss}
              aria-label="Dismiss"
              className="absolute top-2.5 right-2.5 w-7 h-7 rounded-full flex items-center justify-center transition-all active:scale-90"
              style={{ background: 'var(--cs-bg-hover)', color: 'var(--cs-text-muted)' }}
              data-testid="install-prompt-dismiss"
            >
              <X size={13} weight="bold" />
            </button>

            <div className="relative flex items-start gap-3">
              <div
                className="w-11 h-11 rounded-xl shrink-0 flex items-center justify-center"
                style={{
                  background: 'linear-gradient(135deg, #CD040B, #8B0308)',
                  boxShadow: '0 8px 20px -6px rgba(205,4,11,0.6), inset 0 1px 0 rgba(255,255,255,0.18)',
                }}
              >
                <DeviceMobile size={20} weight="fill" className="text-white" />
              </div>
              <div className="min-w-0 flex-1 pr-5">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <Sparkle size={11} weight="fill" style={{ color: '#F59E0B' }} />
                  <span className="text-[9px] font-bold uppercase tracking-[1.5px]" style={{ color: 'var(--cs-text-muted)' }}>
                    {mode === 'ios' ? 'Add to Home Screen' : 'Install the app'}
                  </span>
                </div>
                <div className="text-[15px] font-black leading-tight" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--cs-text)' }}>
                  Keep Connection Source <br className="sm:hidden" />one tap away
                </div>
                <div className="text-[11px] mt-1.5 leading-relaxed" style={{ color: 'var(--cs-text-muted)' }}>
                  {mode === 'ios'
                    ? 'Full-screen, fast launches, push notifications — no browser bar.'
                    : 'Launches instantly, works great offline, sends push alerts.'}
                </div>
              </div>
            </div>

            {mode === 'native' ? (
              <div className="relative mt-3 flex gap-2">
                <button
                  onClick={install}
                  className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-black transition-all active:scale-[0.97]"
                  style={{
                    background: 'linear-gradient(135deg, #CD040B, #8B0308)',
                    color: '#FFF',
                    boxShadow: '0 8px 20px -6px rgba(205,4,11,0.55)',
                    fontFamily: 'Outfit, sans-serif',
                    letterSpacing: '0.5px',
                  }}
                  data-testid="install-prompt-install-btn"
                >
                  <DownloadSimple size={14} weight="bold" />
                  Install
                </button>
                <button
                  onClick={dismiss}
                  className="px-3 py-2.5 rounded-xl text-xs font-bold transition-all active:scale-[0.97]"
                  style={{
                    background: 'var(--cs-bg-hover)',
                    color: 'var(--cs-text-muted)',
                    border: '1px solid var(--cs-border)',
                  }}
                  data-testid="install-prompt-later-btn"
                >
                  Later
                </button>
              </div>
            ) : (
              <div className="relative mt-3">
                <div
                  className="flex items-center gap-2 p-2.5 rounded-xl"
                  style={{
                    background: 'var(--cs-bg-hover)',
                    border: '1px solid var(--cs-border)',
                  }}
                >
                  <StepPill num={1} icon={ShareNetwork} label="Tap Share" />
                  <Arrow />
                  <StepPill num={2} icon={Plus} label="Add to Home" />
                </div>
                <button
                  onClick={dismiss}
                  className="mt-2 w-full py-2 rounded-xl text-[11px] font-bold transition-all active:scale-[0.98]"
                  style={{
                    background: 'var(--cs-bg-hover)',
                    color: 'var(--cs-text-muted)',
                    border: '1px solid var(--cs-border)',
                  }}
                  data-testid="install-prompt-later-btn"
                >
                  Got it
                </button>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function StepPill({ num, icon: Icon, label }) {
  return (
    <div className="flex items-center gap-1.5 flex-1 min-w-0">
      <div
        className="w-6 h-6 rounded-full shrink-0 flex items-center justify-center text-[10px] font-black"
        style={{
          background: 'rgba(205,4,11,0.15)',
          border: '1px solid rgba(205,4,11,0.4)',
          color: '#CD040B',
        }}
      >
        {num}
      </div>
      <Icon size={13} weight="bold" style={{ color: 'var(--cs-text)' }} />
      <span className="text-[10px] font-semibold truncate" style={{ color: 'var(--cs-text)' }}>
        {label}
      </span>
    </div>
  );
}

function Arrow() {
  return (
    <svg width="14" height="8" viewBox="0 0 14 8" fill="none" aria-hidden className="shrink-0">
      <path d="M0 4 H12 M8 1 L12 4 L8 7" stroke="var(--cs-text-muted)" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
